import { Alert, Badge, Box, Button, Center, Container, Loader, Text } from '@mantine/core';
import {
  IconAlertCircle,
  IconEyeOff,
  IconInfoCircle,
  IconPhoto,
  IconPhotoOff,
  IconPlus,
} from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { NotFound } from '~/components/AppLayout/NotFound';
import { Page } from '~/components/AppLayout/Page';
import { ComicCard } from '~/components/Cards/ComicCard';
import { getNsfwLabel } from '~/components/Comics/PanelCard';
import { UserProfileLayout } from '~/components/Profile/ProfileLayout2';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { getEdgeUrl } from '~/client-utils/cf-images-utils';
import { nsfwBrowsingLevelsFlag } from '~/shared/constants/browsingLevel.constants';
import { Flags } from '~/shared/utils/flags';
import { createServerSideProps } from '~/server/utils/server-side-helpers';
import { getUserBannedAtByUsername } from '~/server/services/user-banned-at.service';
import { formatRelativeDate } from '~/utils/comic-helpers';
import type { RouterOutput } from '~/types/router';
import { trpc } from '~/utils/trpc';

export const getServerSideProps = createServerSideProps({
  useSSG: true,
  resolver: async ({ ctx, features, ssg }) => {
    if (!features?.comicCreator) return { notFound: true };

    const username = ctx.query.username as string;
    const user = await getUserBannedAtByUsername(username);
    if (user?.bannedAt)
      return {
        redirect: { destination: `/user/${username}`, permanent: true },
      };

    await Promise.all([
      ssg?.userProfile.get.prefetch({ username }),
      ssg?.userProfile.overview.prefetch({ username }),
    ]);
  },
});

function UserComicsPage() {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const username = (router.query.username as string) ?? '';
  const isSelf = currentUser?.username === username;

  if (!username) return <NotFound />;

  if (!isSelf) {
    return <UserPublicComics username={username} />;
  }

  return <UserOwnComics />;
}

function UserOwnComics() {
  const {
    data: projects,
    isLoading,
    isError,
    refetch,
  } = trpc.comics.getMyProjects.useQuery(undefined);

  return (
    <Container size="xl" py="md">
      <div className="flex justify-between items-center gap-4 mb-3">
        <h2 className="text-lg font-semibold">My Comics</h2>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          component={Link}
          href="/comics/create"
          className="shrink-0"
        >
          New Project
        </Button>
      </div>

      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />} mb="lg">
        <Text size="sm">
          Drafts and pending comics show up here on your profile&apos;s Comics page. Only you can
          see them until you publish a chapter.
        </Text>
      </Alert>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-12 text-gray-400">
          <IconPhotoOff size={48} />
          <p>Failed to load projects</p>
          <Button variant="default" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      ) : projects?.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-gray-400">
          <IconPhotoOff size={48} />
          <p>No projects yet</p>
          <Button leftSection={<IconPlus size={16} />} component={Link} href="/comics/create">
            Create your first comic
          </Button>
        </div>
      ) : (
        <div
          className="grid gap-5"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
        >
          {projects?.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </Container>
  );
}

function UserPublicComics({ username }: { username: string }) {
  const { data: user, isLoading: userLoading } = trpc.userProfile.get.useQuery({ username });

  const {
    data,
    isLoading: comicsLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = trpc.comics.getPublicProjects.useInfiniteQuery(
    { limit: 20, userId: user?.id },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      enabled: !!user?.id,
    }
  );

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const isLoading = userLoading || comicsLoading;

  if (isLoading) {
    return (
      <Box mt="md">
        <Center>
          <Loader />
        </Center>
      </Box>
    );
  }

  if (!user && !userLoading) return <NotFound />;

  return (
    <Container size="xl" py="md">
      {isError ? (
        <div className="flex flex-col items-center gap-3 py-12 text-gray-400">
          <IconAlertCircle size={48} />
          <p>Failed to load comics</p>
          <Button variant="default" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-gray-400">
          <IconPhotoOff size={48} />
          <p>No public comics yet</p>
        </div>
      ) : (
        <>
          <div
            className="grid gap-5"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
          >
            {items.map((comic) => (
              <ComicCard key={comic.id} data={comic} />
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center py-6">
              <Button
                variant="default"
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </Container>
  );
}

type ProjectItem = RouterOutput['comics']['getMyProjects'][number];

function ProjectCard({ project }: { project: ProjectItem }) {
  const features = useFeatureFlags();
  const imageUrl = project.coverImage?.url ?? project.thumbnailUrl;
  const hasNsfw =
    project.nsfwLevel !== 0 && Flags.intersects(project.nsfwLevel, nsfwBrowsingLevelsFlag);
  const isBlocked = features.isGreen && hasNsfw;
  const nsfwInfo = hasNsfw ? getNsfwLabel(project.nsfwLevel) : null;

  return (
    <Link
      href={`/comics/project/${project.id}`}
      className="block overflow-hidden rounded-lg border border-gray-700 bg-gray-800 transition-colors hover:border-gray-500"
    >
      <div className="relative aspect-[3/4] bg-gray-900">
        {isBlocked ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-3">
            <IconEyeOff size={36} className="text-gray-500" />
            <Text size="xs" c="dimmed" ta="center">
              Mature content is not available on this site
            </Text>
            {nsfwInfo && (
              <Badge size="sm" color={nsfwInfo.color} variant="filled">
                {nsfwInfo.label}
              </Badge>
            )}
          </div>
        ) : imageUrl ? (
          <img
            src={getEdgeUrl(imageUrl, { width: 450, optimized: true })}
            alt={project.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <IconPhoto size={36} className="text-gray-600" />
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium truncate">{project.name}</h3>
        <p className="mt-1 text-xs text-gray-400">
          Updated {formatRelativeDate(project.updatedAt)}
        </p>
      </div>
    </Link>
  );
}

export default Page(UserComicsPage, { getLayout: UserProfileLayout });
